import { useRef, useCallback, useEffect } from 'react';
import {
  ref, set, push, remove, get, update,
  onValue, onChildAdded, onChildRemoved,
  onDisconnect, serverTimestamp, off,
} from 'firebase/database';
import throttle from 'lodash.throttle';
import { database } from '@/firebase';
import { getStrokePath, unflattenPoints } from '@utils/strokeUtils';

const MAX_MEMBERS = 8;

export function useFirebaseRoom(dispatch) {
  const listenersRef = useRef([]);
  const roomCodeRef = useRef(null);
  const userIdRef = useRef(null);
  const localStrokeIdsRef = useRef(new Set());
  // Cached member data so we can re-create the member doc on every reconnect.
  // Without this, a brief RTDB disconnect fires onDisconnect().remove() and
  // future stroke writes get PERMISSION_DENIED because the write rule checks
  // that members/{auth.uid} exists.
  const memberDataRef = useRef(null);
  const presenceUnsubRef = useRef(null);

  // Clean up all listeners
  const cleanup = useCallback(() => {
    listenersRef.current.forEach(unsub => unsub());
    listenersRef.current = [];
  }, []);

  // Check if a room exists
  const roomExists = useCallback(async (code) => {
    const snap = await get(ref(database, `rooms/${code}/meta`));
    return snap.exists();
  }, []);

  // Check room status (exists, locked, member count)
  const getRoomStatus = useCallback(async (code) => {
    const [metaSnap, membersSnap] = await Promise.all([
      get(ref(database, `rooms/${code}/meta`)),
      get(ref(database, `rooms/${code}/members`)),
    ]);

    if (!metaSnap.exists()) return { exists: false };

    const meta = metaSnap.val();
    const members = membersSnap.val() || {};
    const memberCount = Object.keys(members).length;

    return {
      exists: true,
      locked: meta.locked || false,
      full: memberCount >= MAX_MEMBERS,
      memberCount,
      roomName: meta.roomName,
      hostHasHostPass: meta.hostHasHostPass || false,
    };
  }, []);

  // Presence heartbeat: watch `.info/connected` and, on every reconnect,
  // re-write the member doc and re-arm onDisconnect. Prevents PERMISSION_DENIED
  // storms after a brief network blip where onDisconnect fired and removed
  // the member, breaking every subsequent stroke write.
  const maintainPresence = useCallback(() => {
    // Clear any existing watcher
    if (presenceUnsubRef.current) {
      presenceUnsubRef.current();
      presenceUnsubRef.current = null;
    }

    const connectedRef = ref(database, '.info/connected');
    const unsub = onValue(connectedRef, async (snap) => {
      const isConnected = snap.val();
      if (!isConnected) return;

      const code = roomCodeRef.current;
      const userId = userIdRef.current;
      const memberData = memberDataRef.current;
      if (!code || !userId || !memberData) return;

      try {
        const memberRef = ref(database, `rooms/${code}/members/${userId}`);
        const cursorRef = ref(database, `rooms/${code}/cursors/${userId}`);
        // Cancel any stale onDisconnect first, then re-write and re-arm.
        await onDisconnect(memberRef).cancel();
        await set(memberRef, memberData);
        await onDisconnect(memberRef).remove();
        await onDisconnect(cursorRef).cancel();
        await onDisconnect(cursorRef).remove();
      } catch (err) {
        console.warn('[presence] Failed to re-establish member doc:', err);
      }
    });

    presenceUnsubRef.current = () => off(connectedRef, 'value', unsub);
  }, []);

  // Create a new room (separate writes so RTDB rules can validate each node).
  // hostHasHostPass is baked into room metadata so joining clients can see
  // whether they're allowed to become the 4th, 5th... player.
  const createRoom = useCallback(async (code, roomName, userId, nickname, colour, hostHasHostPass = false) => {
    const now = Date.now();
    // Write meta first (rule allows write when !data.exists())
    await set(ref(database, `rooms/${code}/meta`), {
      createdAt: now,
      hostId: userId,
      roomName,
      locked: false,
      lastActivity: now,
      hostHasHostPass: !!hostHasHostPass,
    });
    // Then write own member entry
    const memberPayload = {
      nickname,
      joinedAt: now,
      isHost: true,
      colour,
    };
    await set(ref(database, `rooms/${code}/members/${userId}`), memberPayload);
    roomCodeRef.current = code;
    userIdRef.current = userId;
    memberDataRef.current = memberPayload;
  }, []);

  // Update the host's Host Pass status on an existing room.
  // Called when the host signs in after creating the room, or their
  // entitlements change mid-session.
  const updateHostPassStatus = useCallback(async (hasHostPass) => {
    const code = roomCodeRef.current;
    if (!code) return;
    try {
      await update(ref(database, `rooms/${code}/meta`), { hostHasHostPass: !!hasHostPass });
    } catch (err) {
      console.warn('[createRoom] Failed to update host pass status:', err);
    }
  }, []);

  // Join an existing room
  const joinRoom = useCallback(async (code, userId, nickname, colour) => {
    const memberRef = ref(database, `rooms/${code}/members/${userId}`);
    const memberPayload = {
      nickname,
      joinedAt: Date.now(),
      isHost: false,
      colour,
    };
    await set(memberRef, memberPayload);
    roomCodeRef.current = code;
    userIdRef.current = userId;
    memberDataRef.current = memberPayload;

    // Set up disconnect handlers
    const cursorRef = ref(database, `rooms/${code}/cursors/${userId}`);
    onDisconnect(memberRef).remove();
    onDisconnect(cursorRef).remove();
  }, []);

  // Set up disconnect handlers + start the reconnect watcher.
  // Called after both createRoom and joinRoom.
  const setupDisconnect = useCallback((code, userId) => {
    const memberRef = ref(database, `rooms/${code}/members/${userId}`);
    const cursorRef = ref(database, `rooms/${code}/cursors/${userId}`);
    onDisconnect(memberRef).remove();
    onDisconnect(cursorRef).remove();
    // Start presence heartbeat so a brief blip doesn't break stroke writes
    maintainPresence();
  }, [maintainPresence]);

  // Start all listeners for a room
  const startListeners = useCallback((code, userId) => {
    roomCodeRef.current = code;
    userIdRef.current = userId;
    localStrokeIdsRef.current = new Set();

    // Listen to meta
    const metaRef = ref(database, `rooms/${code}/meta`);
    const metaUnsub = onValue(metaRef, (snap) => {
      if (snap.exists()) {
        dispatch({ type: 'SET_ROOM_META', payload: snap.val() });
      }
    });
    listenersRef.current.push(() => off(metaRef, 'value', metaUnsub));

    // Listen to members
    const membersRef = ref(database, `rooms/${code}/members`);
    let prevMembers = {};
    const membersUnsub = onValue(membersRef, (snap) => {
      const members = snap.val() || {};
      dispatch({ type: 'SET_MEMBERS', payload: members });

      // Detect joins/leaves for toasts
      const prevIds = new Set(Object.keys(prevMembers));
      const currIds = new Set(Object.keys(members));

      for (const id of currIds) {
        if (!prevIds.has(id) && id !== userId) {
          dispatch({
            type: 'ADD_TOAST',
            payload: { message: `${members[id].nickname} joined the room`, colour: members[id].colour },
          });
        }
      }
      for (const id of prevIds) {
        if (!currIds.has(id) && id !== userId) {
          dispatch({
            type: 'ADD_TOAST',
            payload: { message: `${prevMembers[id].nickname} left the room` },
          });
        }
      }
      prevMembers = members;

      // Host fallback: if hostId is no longer in members, the earliest
      // joiner promotes themselves (only they write, avoiding permission errors)
      get(ref(database, `rooms/${code}/meta/hostId`)).then((hostSnap) => {
        const currentHostId = hostSnap.val();
        if (currentHostId && !members[currentHostId]) {
          const sorted = Object.entries(members).sort(
            ([, a], [, b]) => a.joinedAt - b.joinedAt
          );
          if (sorted.length > 0) {
            const [newHostId] = sorted[0];
            // Only the new host writes the promotion — others skip
            if (newHostId === userId) {
              update(ref(database, `rooms/${code}/meta`), { hostId: newHostId });
              update(ref(database, `rooms/${code}/members/${newHostId}`), { isHost: true });
            }
          }
        }
      });
    });
    listenersRef.current.push(() => off(membersRef, 'value', membersUnsub));

    // Listen to strokes (added)
    const strokesRef = ref(database, `rooms/${code}/strokes`);
    const strokeAddUnsub = onChildAdded(strokesRef, (snap) => {
      const strokeId = snap.key;
      // Skip if we already have this stroke locally (our own committed stroke)
      if (localStrokeIdsRef.current.has(strokeId)) return;

      const data = snap.val();

      // Non-freehand tools don't need path computation
      const noPathTools = ['shape', 'stamp', 'fill', 'text', 'bezier', 'gradient'];
      if (noPathTools.includes(data.tool)) {
        dispatch({ type: 'ADD_STROKE', payload: { id: strokeId, ...data } });
        return;
      }

      const inputPoints = unflattenPoints(data.points);
      const pathData = getStrokePath(inputPoints, data.tool, data.size);

      dispatch({
        type: 'ADD_STROKE',
        payload: {
          id: strokeId,
          ...data,
          pathData,
        },
      });
    });
    listenersRef.current.push(() => off(strokesRef, 'child_added', strokeAddUnsub));

    // Listen to strokes (removed — for undo)
    const strokeRemoveUnsub = onChildRemoved(strokesRef, (snap) => {
      dispatch({ type: 'REMOVE_STROKE', payload: snap.key });
    });
    listenersRef.current.push(() => off(strokesRef, 'child_removed', strokeRemoveUnsub));

    // Listen to cursors
    const cursorsRef = ref(database, `rooms/${code}/cursors`);
    const cursorsUnsub = onValue(cursorsRef, (snap) => {
      const cursors = snap.val() || {};
      // Filter out our own cursor
      delete cursors[userId];
      dispatch({ type: 'SET_CURSORS', payload: cursors });
    });
    listenersRef.current.push(() => off(cursorsRef, 'value', cursorsUnsub));

    // Listen to events
    const eventsRef = ref(database, `rooms/${code}/events`);
    let eventsInitialised = false;
    // Skip existing events on initial load
    get(eventsRef).then(() => { eventsInitialised = true; });
    const eventsUnsub = onChildAdded(eventsRef, (snap) => {
      if (!eventsInitialised) return;
      const event = snap.val();

      if (event.type === 'clear') {
        dispatch({ type: 'CLEAR_ALL_STROKES' });
        dispatch({
          type: 'ADD_TOAST',
          payload: { message: 'Canvas cleared by host' },
        });
      }

      if (event.type === 'kick' && event.targetUserId === userId) {
        dispatch({
          type: 'ADD_TOAST',
          payload: { message: 'You have been removed from this room' },
        });
        // Small delay so toast is visible before redirect
        setTimeout(() => {
          dispatch({ type: 'SET_SCREEN', payload: 'landing' });
        }, 1500);
      }
    });
    listenersRef.current.push(() => off(eventsRef, 'child_added', eventsUnsub));

    // Listen to game state
    // Listen to game state — use shallow comparison to avoid re-dispatching
    // on every RTDB echo when only image data changed (images are large)
    const gameRef = ref(database, `rooms/${code}/game`);
    let prevGameMode = null;
    let prevPhase = null;
    let prevRound = null;
    let prevCurrentDrawerId = null;
    let prevTimerEndsAt = null;
    let prevCurrentZone = null;
    let prevCurrentCreature = null;
    let prevCurrentPairIndex = null;
    let prevCurrentRound = null;
    let lastFullDispatch = 0;
    const FULL_DISPATCH_THROTTLE = 500; // ms — avoid rapid full re-dispatches

    const gameUnsub = onValue(gameRef, (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        // If the mode changed (new game started), clear first to prevent stale fields
        if (data.mode !== prevGameMode) {
          dispatch({ type: 'CLEAR_GAME' });
          prevGameMode = data.mode;
          prevPhase = null;
        }

        // Check if key game control fields changed.
        // currentZone and currentCreature are included for Exquisite Corpse —
        // zone transitions must dispatch immediately so all clients update.
        const controlChanged = data.phase !== prevPhase
          || data.round !== prevRound
          || data.currentDrawerId !== prevCurrentDrawerId
          || data.timerEndsAt !== prevTimerEndsAt
          || data.currentZone !== prevCurrentZone
          || data.currentCreature !== prevCurrentCreature
          || data.currentPairIndex !== prevCurrentPairIndex
          || data.currentRound !== prevCurrentRound;

        if (controlChanged) {
          // Key fields changed — dispatch immediately
          prevPhase = data.phase;
          prevRound = data.round;
          prevCurrentDrawerId = data.currentDrawerId;
          prevTimerEndsAt = data.timerEndsAt;
          prevCurrentZone = data.currentZone;
          prevCurrentCreature = data.currentCreature;
          prevCurrentPairIndex = data.currentPairIndex;
          prevCurrentRound = data.currentRound;
          lastFullDispatch = Date.now();
          dispatch({ type: 'SET_GAME_STATE', payload: data });
        } else {
          // Only secondary data changed (submissions, votes, reactions, images)
          // Throttle to avoid rapid re-renders from each vote/submission
          const now = Date.now();
          if (now - lastFullDispatch > FULL_DISPATCH_THROTTLE) {
            lastFullDispatch = now;
            dispatch({ type: 'SET_GAME_STATE', payload: data });
          }
        }
      } else {
        dispatch({ type: 'CLEAR_GAME' });
        prevGameMode = null;
        prevPhase = null;
      }
    });
    listenersRef.current.push(() => off(gameRef, 'value', gameUnsub));
  }, [dispatch]);

  // Throttled cursor write (180ms = ~5.5 updates/sec, still smooth, 33% less bandwidth)
  const updateCursorThrottled = useRef(
    throttle((code, userId, x, y, nickname, colour, tool) => {
      set(ref(database, `rooms/${code}/cursors/${userId}`), {
        x, y, nickname, colour, tool,
      });
    }, 180)
  ).current;

  // Track pointer activity to pause cursor broadcasts when idle
  const lastCursorMoveRef = useRef(0);
  const lastCursorPosRef = useRef({ x: null, y: null });

  const updateCursor = useCallback((x, y, nickname, colour, tool) => {
    if (!roomCodeRef.current || !userIdRef.current) return;
    // Skip if position unchanged (pointer hovering stationary)
    if (lastCursorPosRef.current.x === x && lastCursorPosRef.current.y === y) return;
    lastCursorPosRef.current = { x, y };
    lastCursorMoveRef.current = Date.now();
    updateCursorThrottled(roomCodeRef.current, userIdRef.current, x, y, nickname, colour, tool);
  }, [updateCursorThrottled]);

  // Push a completed stroke to RTDB — resilient to RTDB errors.
  // If the write fails with PERMISSION_DENIED, the member doc has likely
  // been removed by a stale onDisconnect (flaky connection). Self-heal by
  // re-writing the member doc and retrying once.
  const pushStroke = useCallback(async (strokeData) => {
    const code = roomCodeRef.current;
    const userId = userIdRef.current;
    if (!code) return null;

    // Strip undefined/null values that Firebase rejects
    const cleaned = {};
    for (const [key, value] of Object.entries(strokeData)) {
      if (value !== undefined && value !== null) cleaned[key] = value;
    }

    const tryWrite = async () => {
      const strokeRef = push(ref(database, `rooms/${code}/strokes`));
      const strokeId = strokeRef.key;
      localStrokeIdsRef.current.add(strokeId);
      try {
        await set(strokeRef, cleaned);
        return { strokeId, error: null };
      } catch (err) {
        localStrokeIdsRef.current.delete(strokeId);
        return { strokeId: null, error: err };
      }
    };

    let result = await tryWrite();
    if (result.strokeId) return result.strokeId;

    // Retry path: if permission denied and we have cached member data,
    // the member doc is probably gone — re-create it and retry once.
    const isPermissionDenied =
      result.error?.message?.includes('PERMISSION_DENIED') ||
      result.error?.code === 'PERMISSION_DENIED';

    if (isPermissionDenied && userId && memberDataRef.current) {
      try {
        const memberRef = ref(database, `rooms/${code}/members/${userId}`);
        const cursorRef = ref(database, `rooms/${code}/cursors/${userId}`);
        await onDisconnect(memberRef).cancel();
        await set(memberRef, memberDataRef.current);
        await onDisconnect(memberRef).remove();
        await onDisconnect(cursorRef).remove();
        console.log('[pushStroke] Recovered member doc after PERMISSION_DENIED');
        result = await tryWrite();
        if (result.strokeId) return result.strokeId;
      } catch (recoveryErr) {
        console.warn('[pushStroke] Self-heal failed:', recoveryErr);
      }
    }

    console.error('[pushStroke] Failed to write stroke:', result.error, cleaned);
    return null;
  }, []);

  // Update specific fields on an existing stroke in RTDB
  const updateStrokeInDB = useCallback(async (strokeId, updates) => {
    const code = roomCodeRef.current;
    if (!code || !strokeId) return;
    await update(ref(database, `rooms/${code}/strokes/${strokeId}`), updates);
  }, []);

  // Remove a stroke (for undo)
  const removeStroke = useCallback(async (strokeId) => {
    const code = roomCodeRef.current;
    if (!code) return;
    localStrokeIdsRef.current.delete(strokeId);
    await remove(ref(database, `rooms/${code}/strokes/${strokeId}`));
  }, []);

  // Push an event (clear, kick, etc.)
  const pushEvent = useCallback(async (eventData) => {
    const code = roomCodeRef.current;
    if (!code) return;
    await push(ref(database, `rooms/${code}/events`), {
      ...eventData,
      timestamp: Date.now(),
    });
  }, []);

  // Clear all strokes from RTDB
  const clearAllStrokes = useCallback(async () => {
    const code = roomCodeRef.current;
    if (!code) return;
    await remove(ref(database, `rooms/${code}/strokes`));
    localStrokeIdsRef.current.clear();
  }, []);

  // Restore an array of strokes to RTDB (used when a game ends and the
  // host wants to put the pre-game canvas back for everyone).
  // Each stroke gets a fresh auto-generated ID to avoid collisions.
  const restoreStrokes = useCallback(async (strokes) => {
    const code = roomCodeRef.current;
    if (!code || !Array.isArray(strokes) || strokes.length === 0) return;
    try {
      await Promise.all(strokes.map(async (stroke) => {
        // Strip undefined/null and the old id/pathData before writing
        const { id, pathData, ...rest } = stroke;
        const cleaned = {};
        for (const [key, value] of Object.entries(rest)) {
          if (value !== undefined && value !== null) cleaned[key] = value;
        }
        const strokeRef = push(ref(database, `rooms/${code}/strokes`));
        localStrokeIdsRef.current.add(strokeRef.key);
        await set(strokeRef, cleaned);
      }));
    } catch (err) {
      console.warn('[restoreStrokes] Failed to restore some strokes:', err);
    }
  }, []);

  // Kick a user
  const kickUser = useCallback(async (targetUserId) => {
    const code = roomCodeRef.current;
    if (!code) return;
    await remove(ref(database, `rooms/${code}/members/${targetUserId}`));
    await remove(ref(database, `rooms/${code}/cursors/${targetUserId}`));
  }, []);

  // Update room meta (lock/unlock, etc.)
  const updateRoomMeta = useCallback(async (updates) => {
    const code = roomCodeRef.current;
    if (!code) return;
    await update(ref(database, `rooms/${code}/meta`), updates);
  }, []);

  // --- Game mode functions ---

  // Set full game state (for initialisation)
  const setGameState = useCallback(async (data) => {
    const code = roomCodeRef.current;
    if (!code) return;
    await set(ref(database, `rooms/${code}/game`), data);
  }, []);

  // Partial update on game state (skips if game node was already removed)
  const updateGameState = useCallback(async (updates) => {
    const code = roomCodeRef.current;
    if (!code) return;
    try {
      await update(ref(database, `rooms/${code}/game`), updates);
    } catch (err) {
      // Silently ignore permission errors from writing to a removed game node
      if (!err?.message?.includes('PERMISSION_DENIED')) throw err;
    }
  }, []);

  // Submit a challenge mode entry (stores Firebase Storage URL, not base64)
  const submitChallengeEntry = useCallback(async (userId, imageUrl, nickname) => {
    const code = roomCodeRef.current;
    if (!code || !imageUrl) return;
    await set(ref(database, `rooms/${code}/game/submissions/${userId}`), {
      imageData: imageUrl, // field name kept for backward compatibility
      nickname: (nickname || '').slice(0, 30),
      submittedAt: Date.now(),
      isDrawoff: false,
    });
  }, []);

  // Submit a vote in challenge mode (validated 0-10 range)
  const submitVote = useCallback(async (voterId, targetUserId, score) => {
    const code = roomCodeRef.current;
    if (!code) return;
    const validScore = Math.max(0, Math.min(10, Number(score) || 0));
    await set(ref(database, `rooms/${code}/game/votes/${voterId}/${targetUserId}`), validScore);
  }, []);

  // Mark voter as done (with timestamp)
  const markVotingComplete = useCallback(async (voterId) => {
    const code = roomCodeRef.current;
    if (!code) return;
    await set(ref(database, `rooms/${code}/game/votes/${voterId}/submittedAt`), Date.now());
  }, []);

  // Push a spectator reaction (relay mode, validated)
  const pushReaction = useCallback(async (emoji, userId) => {
    const code = roomCodeRef.current;
    if (!code || !emoji || typeof emoji !== 'string') return;
    await push(ref(database, `rooms/${code}/game/reactions`), {
      emoji: emoji.slice(0, 4),
      fromUserId: userId,
      timestamp: Date.now(),
    });
  }, []);

  // Clear reactions (between turns)
  const clearReactions = useCallback(async () => {
    const code = roomCodeRef.current;
    if (!code) return;
    await remove(ref(database, `rooms/${code}/game/reactions`));
  }, []);

  // Remove entire game state
  const clearGame = useCallback(async () => {
    const code = roomCodeRef.current;
    if (!code) return;
    await remove(ref(database, `rooms/${code}/game`));
  }, []);

  // Leave room
  const leaveRoom = useCallback(async () => {
    const code = roomCodeRef.current;
    const userId = userIdRef.current;
    if (!code || !userId) return;
    cleanup();
    // Stop presence heartbeat so it can't re-create the member doc
    // after we've intentionally left.
    if (presenceUnsubRef.current) {
      presenceUnsubRef.current();
      presenceUnsubRef.current = null;
    }
    memberDataRef.current = null;
    // Cancel any pending onDisconnect handlers before removing so the
    // server doesn't try to remove a node that's already gone.
    try {
      await onDisconnect(ref(database, `rooms/${code}/members/${userId}`)).cancel();
      await onDisconnect(ref(database, `rooms/${code}/cursors/${userId}`)).cancel();
    } catch { /* ignore */ }
    await remove(ref(database, `rooms/${code}/members/${userId}`));
    await remove(ref(database, `rooms/${code}/cursors/${userId}`));
    roomCodeRef.current = null;
    userIdRef.current = null;
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (presenceUnsubRef.current) {
        presenceUnsubRef.current();
        presenceUnsubRef.current = null;
      }
    };
  }, [cleanup]);

  return {
    roomExists,
    getRoomStatus,
    createRoom,
    updateHostPassStatus,
    joinRoom,
    setupDisconnect,
    startListeners,
    updateCursor,
    pushStroke,
    updateStrokeInDB,
    removeStroke,
    pushEvent,
    clearAllStrokes,
    restoreStrokes,
    kickUser,
    updateRoomMeta,
    setGameState,
    updateGameState,
    submitChallengeEntry,
    submitVote,
    markVotingComplete,
    pushReaction,
    clearReactions,
    clearGame,
    leaveRoom,
  };
}
